/**
 * Group key service — manages the lifecycle of symmetric group encryption keys.
 *
 * Pattern copied from spacelinq-android DefaultGroupKeysMessageHandler:
 *   - Group owner generates one AES-256 symmetric key per group
 *   - Owner distributes it to each member as a 1:1 ECDH-encrypted message
 *   - Members decrypt and store the key; all subsequent group messages use it
 *
 * Group key distribution wire format (sent as a 1:1 message to each member):
 *   JSON { cls: "group_key", group_name: string, content: string }
 *   where `content` = encryptMessage(JSON{ group_name, symmetric_key, group_owner_username })
 *   i.e. the same ECDH wire format used for 1:1 messages: IV:ciphertext:signature
 *
 * Matches Android's GroupKeyContentSerializable:
 *   { group_name, symmetric_key, group_owner_username }
 */

import {
  generateSymmetricKey,
  encryptMessage,
  decryptMessage,
} from "@/utils/crypto";
import {
  storeSymmetricKey,
  getSymmetricKey,
  getActiveKeySet,
  getPublicKeySetByUsername,
  PeerPublicKeySet,
} from "@/services/keyStorageService";
import { requestPublicKey, refreshPublicKey, wasRefreshedThisSession } from "@/services/keyExchangeService";
import { sendUserMessage, fetchReceivedSince } from "@/services/conversationService";
import { getGroups } from "@/services/userGroupService";
import { Group, GroupMember } from "@/types/users";
import { USER_ADDRESS_TYPE } from "@/constants/message";
import { isExternalDmAddress } from "@/utils/addresses";

// Owner-side answer dedup is state, not timers: kept in localStorage (shared by tabs, survives
// reloads) and checked under a Web Lock so tabs receiving the same request don't both answer.
// Both are best-effort — without them this degrades to per-tab behaviour.
function readRecord<T>(key: string): Record<string, T> {
  try { return JSON.parse(localStorage.getItem(key) ?? "{}") ?? {}; } catch { return {}; }
}

function writeRecord<T>(key: string, record: Record<string, T>): void {
  try { localStorage.setItem(key, JSON.stringify(record)); } catch { /* storage unavailable */ }
}

async function withLock(name: string, fn: () => Promise<void>): Promise<void> {
  if (navigator.locks) await navigator.locks.request(name, fn);
  else await fn(); // navigator.locks is absent in insecure contexts
}

// cls field values — must match Android's ProtobufEncryptedGroupKeyMessageContentDetails.CLS
// and ProtobufGroupKeyRequestMessageContentDetails.CLS
export const GROUP_KEY_CLS = "encrypted_group_key";
export const GROUP_KEY_REQUEST_CLS = "group_key_request";

export interface GroupKeyDistributionMessage {
  cls: "encrypted_group_key";
  group_name: string;
  content: string; // ECDH wire string: IV:ciphertext:signature
}

export interface GroupKeyRequestMessage {
  cls: "group_key_request";
  group_id: number;
}

// Inner JSON payload (matches Android's GroupKeyContentSerializable)
interface GroupKeyPayload {
  group_name: string;
  symmetric_key: string;
  group_owner_username: string;
}

// ---------------------------------------------------------------------------
// Address normalization
// ---------------------------------------------------------------------------

/**
 * Normalizes a group routing address to the canonical base form {"gid":"X","mid":"0"}.
 *
 * Group addresses are per-member JSONs like {"gid":"1","mid":"42"} where mid is the
 * sender's user ID. The symmetric key is keyed by the GROUP (gid), not the sender,
 * so we always strip mid to "0" before any IDB lookup or store. Without this,
 * messages from different senders produce different address strings and the key
 * lookup misses even though the key was stored correctly.
 */
export function normalizeGroupAddress(address: string): string {
  try {
    const parsed = JSON.parse(address);
    if (parsed && typeof parsed.gid !== "undefined") {
      return JSON.stringify({ gid: String(parsed.gid), mid: "0" });
    }
  } catch {
    // not a group JSON — return as-is
  }
  return address;
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

export function isGroupKeyDistributionMessage(message: string): boolean {
  try {
    const p = JSON.parse(message);
    return p?.cls === GROUP_KEY_CLS && typeof p.group_name === "string" && typeof p.content === "string";
  } catch {
    return false;
  }
}

export function parseGroupKeyDistributionMessage(
  message: string
): GroupKeyDistributionMessage | null {
  try {
    const p = JSON.parse(message);
    if (p?.cls === GROUP_KEY_CLS && p.group_name && p.content) {
      return p as GroupKeyDistributionMessage;
    }
    return null;
  } catch {
    return null;
  }
}

export function isGroupKeyRequestMessage(message: string): boolean {
  try {
    const p = JSON.parse(message);
    // Require the cls marker AND a numeric group_id (matches parseGroupKeyRequestMessage)
    // so a bare/malformed request isn't misclassified as key exchange.
    return p?.cls === GROUP_KEY_REQUEST_CLS && typeof p.group_id === "number";
  } catch {
    return false;
  }
}

export function parseGroupKeyRequestMessage(message: string): GroupKeyRequestMessage | null {
  try {
    const p = JSON.parse(message);
    if (p?.cls === GROUP_KEY_REQUEST_CLS && typeof p.group_id === "number") {
      return p as GroupKeyRequestMessage;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Owner: generate + distribute
// ---------------------------------------------------------------------------

/**
 * Returns the existing symmetric key for a group, or generates and stores a
 * new one if none exists. Only call this as the group owner.
 */
export async function getOrGenerateGroupSymmetricKey(
  groupAddress: string,
  groupName: string,
  ownerUsername: string
): Promise<string> {
  const canonical = normalizeGroupAddress(groupAddress);
  const existing = await getSymmetricKey(canonical);
  if (existing) return existing.symmetricKeyB64;

  const symmetricKeyB64 = await generateSymmetricKey();
  await storeSymmetricKey(canonical, groupName, ownerUsername, symmetricKeyB64);
  console.log(`[GroupKey] Generated symmetric key for group "${groupName}"`);
  return symmetricKeyB64;
}

/**
 * Creates the JSON string that is sent as a 1:1 message from the owner to
 * one group member, containing the group's symmetric key encrypted for that
 * member using ECDH (same algorithm as regular 1:1 messages).
 */
async function createGroupKeyDistributionContent(
  groupName: string,
  symmetricKeyB64: string,
  ownerUsername: string,
  ownEncPrivKeyJwk: JsonWebKey,
  ownSignPrivKeyJwk: JsonWebKey,
  recipientEncPubKeyJwk: JsonWebKey
): Promise<string> {
  const payload: GroupKeyPayload = {
    group_name: groupName,
    symmetric_key: symmetricKeyB64,
    group_owner_username: ownerUsername,
  };

  const encryptedContent = await encryptMessage(
    JSON.stringify(payload),
    recipientEncPubKeyJwk,
    ownEncPrivKeyJwk,
    ownSignPrivKeyJwk
  );

  const msg: GroupKeyDistributionMessage = {
    cls: GROUP_KEY_CLS,
    group_name: groupName,
    content: encryptedContent,
  };

  return JSON.stringify(msg);
}

/**
 * Sends the group's symmetric key to every member whose public key is in
 * IndexedDB. Members without keys yet are skipped — they'll get the key
 * once they share their own public key and the owner redistributes.
 *
 * @param groupAddress  The group's routing address (used as the key identifier)
 * @param groupName     Human-readable group name
 * @param ownerUsername Current user's username (must be the owner)
 * @param members       All group members (from Group.members)
 * @param userId        Current user's numeric ID
 */
export async function distributeGroupKeyToMembers(
  groupAddress: string,
  groupName: string,
  ownerUsername: string,
  members: GroupMember[],
  userId: number
): Promise<void> {
  const ownKeySet = await getActiveKeySet(userId);
  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot distribute — no own key set");
    return;
  }

  const symmetricKeyEntry = await getSymmetricKey(normalizeGroupAddress(groupAddress));
  if (!symmetricKeyEntry) {
    console.warn("[GroupKey] Cannot distribute — no symmetric key stored for group");
    return;
  }

  const { symmetricKeyB64 } = symmetricKeyEntry;

  // Filter to non-owner members (owner already has the key locally)
  const recipients = members.filter((m) => !m.isOwner && m.username !== ownerUsername);

  // Collect each recipient's public encryption key to ECDH-wrap the symmetric key for them.
  // Members without a key yet are skipped — they pull it later via group_key_request.
  const memberKeyMap = new Map<string, PeerPublicKeySet>();
  for (const member of recipients) {
    let peerKeys = await getPublicKeySetByUsername(member.username);
    if (!peerKeys) {
      // Key not in IDB cache — fetch from server. This is expected when the owner
      // is on a fresh browser session (cleared IDB) or the member re-logged in.
      try {
        await requestPublicKey(member.username, userId);
        peerKeys = await getPublicKeySetByUsername(member.username);
      } catch (err) {
        console.debug(`[GroupKey] Could not fetch key for ${member.username}:`, err);
      }
    }
    if (peerKeys) {
      memberKeyMap.set(member.username, peerKeys);
    } else {
      console.debug(`[GroupKey] Skipping ${member.username} — no public key after fetch attempt`);
    }
  }

  // Send distribution to each member who has keys.
  let distributed = 0;
  for (const member of recipients) {
    const peerKeys = memberKeyMap.get(member.username);
    if (!peerKeys) continue;

    try {
      const msgContent = await createGroupKeyDistributionContent(
        groupName,
        symmetricKeyB64,
        ownerUsername,
        ownKeySet.encryptPrivateKeyJwk,
        ownKeySet.signPrivateKeyJwk,
        peerKeys.encryptPublicKeyJwk
      );

      await sendUserMessage({
        cmd: "SendUserMessage",
        message: msgContent,
        address: member.username,
        addressId: 0,
        address_type: USER_ADDRESS_TYPE,
        lat: 0,
        lng: 0,
        media: null,
        media_filename: null,
        media_type: 0,
        sendtime: Date.now(),
        userid: userId,
        // Required: Android's DefaultMessageContentAdapter.responseToContent only
        // parses the inner ECDH-encrypted symmetric key out of `cls:encrypted_group_key`
        // when r_encrypted=1. Without this flag Android non-owners never get the key.
        encrypted: 1,
      });

      distributed++;
      console.log(`[GroupKey] Distributed key for "${groupName}" to ${member.username}`);
    } catch (err) {
      console.error(`[GroupKey] Failed to distribute to ${member.username}:`, err);
    }
  }

  console.log(`[GroupKey] Distributed to ${distributed}/${recipients.length} members`);
}

// ---------------------------------------------------------------------------
// Member: receive + store
// ---------------------------------------------------------------------------

/** The group with this routing address (any per-member variant), from the cached groups list. */
export async function findGroupByAddress(address: string): Promise<Group | undefined> {
  const canonical = normalizeGroupAddress(address);
  return (await getGroups()).find((g) => normalizeGroupAddress(g.address) === canonical);
}

/** A group by name (distributions carry no id); the owner disambiguates same-named groups. */
async function resolveGroupByName(groupName: string, ownerUsername?: string): Promise<Group | null> {
  try {
    let groups = await getGroups();
    let byName = groups.filter((g) => g.name === groupName);
    if (byName.length === 0) {
      // Group list may be stale (group created/renamed since last fetch) — force refresh.
      groups = await getGroups({ force: true });
      byName = groups.filter((g) => g.name === groupName);
    }
    const ownerMatches = byName.filter((g) => !ownerUsername || g.ownerName === ownerUsername);
    if (ownerMatches.length > 1) {
      // Same-name/same-owner groups are indistinguishable — take the first; a wrong pick
      // self-heals via re-request.
      console.warn(`[GroupKey] Ambiguous group "${groupName}" (owner "${ownerUsername}"): ${ownerMatches.length} groups match — using the first.`);
    }
    return (byName.length === 1 ? byName[0] : ownerMatches[0]) ?? null;
  } catch (err) {
    console.warn("[GroupKey] Could not resolve canonical group address", err);
    return null;
  }
}

/**
 * Decrypts an incoming group key distribution message and stores the
 * symmetric key in IndexedDB for future group message decryption.
 *
 * @param parsed  The parsed group key distribution message
 * @param userId  Current user's ID (to load own private key)
 * @param ownerUsername  Username of the message sender (the group owner)
 * @returns The canonical group address the key was stored under, or null if it was dropped.
 */
export async function receiveGroupKeyDistributionMessage(
  parsed: GroupKeyDistributionMessage,
  userId: number,
  ownerUsername: string
): Promise<string | null> {
  // Any answer ends our waiting request — even one we fail to unwrap below.
  const answeredFor = await resolveGroupByName(parsed.group_name, ownerUsername);
  if (answeredFor) clearPendingGroupKeyRequest(normalizeGroupAddress(answeredFor.address));

  let [ownKeySet, ownerKeys] = await Promise.all([
    getActiveKeySet(userId),
    getPublicKeySetByUsername(ownerUsername),
  ]);

  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot receive key — no own key set");
    return null;
  }

  // The distribution is ECDH-encrypted: we need the owner's public key to derive
  // the shared secret. If it's missing the distribution arrived before the @keys
  // response — fetch it now and wait (up to the requestPublicKey timeout).
  if (!ownerKeys) {
    console.warn(`[GroupKey] Owner key for "${ownerUsername}" not cached — fetching`);
    try {
      await requestPublicKey(ownerUsername, userId);
      ownerKeys = await getPublicKeySetByUsername(ownerUsername);
    } catch {
      // timeout or network error — fall through to the null guard below
    }
  }

  if (!ownerKeys) {
    console.warn(`[GroupKey] Cannot receive key — no public key for owner "${ownerUsername}" after fetch attempt`);
    return null;
  }

  // Decrypt the ECDH-wrapped payload. If it fails, our cached owner key may be stale (the owner
  // re-keyed, e.g. moved to a new device) — re-fetch it once and retry before giving up. Without
  // this, a stale-but-present owner key loops forever: decrypt fails → drop → re-request → same
  // stale key → fail again. Mirrors the owner-side stale-key re-fetch.
  let payloadJson: string;
  try {
    payloadJson = await decryptMessage(
      parsed.content,
      ownKeySet.encryptPrivateKeyJwk,
      ownerKeys.encryptPublicKeyJwk
    );
  } catch {
    console.warn(`[GroupKey] Decrypt failed with cached key for owner "${ownerUsername}" — re-fetching and retrying`);
    await requestPublicKey(ownerUsername, userId).catch(() => {});
    const freshOwnerKeys = await getPublicKeySetByUsername(ownerUsername);
    if (!freshOwnerKeys) {
      console.error(`[GroupKey] Cannot receive key — no fresh public key for owner "${ownerUsername}"`);
      return null;
    }
    payloadJson = await decryptMessage(
      parsed.content,
      ownKeySet.encryptPrivateKeyJwk,
      freshOwnerKeys.encryptPublicKeyJwk
    );
  }

  const payload: GroupKeyPayload = JSON.parse(payloadJson);
  const group = await resolveGroupByName(payload.group_name, payload.group_owner_username);
  const canonicalAddress = group && normalizeGroupAddress(group.address);

  if (!canonicalAddress) {
    // Don't store under a divergent fallback — that's what caused split-brain. A
    // missing key surfaces as "key not available" and triggers a reactive re-request.
    console.error(
      `[GroupKey] Could not resolve canonical address for group "${payload.group_name}" (owner "${payload.group_owner_username}") — dropping key to avoid split-brain. It will be re-requested.`
    );
    return null;
  }

  // Always overwrite — the owner is authoritative; the latest distribution wins.
  await storeSymmetricKey(
    canonicalAddress,
    payload.group_name,
    payload.group_owner_username,
    payload.symmetric_key
  );

  console.log(
    `[GroupKey] Stored symmetric key for group "${payload.group_name}" from owner "${payload.group_owner_username}" under ${canonicalAddress}`
  );

  return canonicalAddress;
}

/**
 * Returns the symmetric key for a group, keyed by its canonical routing address.
 */
export async function resolveGroupSymmetricKey(
  groupAddress: string
): Promise<string | null> {
  // Normalize to the base address (mid="0") so keys are found regardless of which
  // per-member address variant ({"gid":X,"mid":Y}) the caller passes in.
  const canonical = normalizeGroupAddress(groupAddress);
  return (await getSymmetricKey(canonical))?.symmetricKeyB64 ?? null;
}

/**
 * Once per session, refreshes the key a conversation's history is labelled against — the peer's
 * for a DM, the owner's for a group we don't own — before that history is decrypted. Otherwise
 * the first load races ChatInputs' refresh and old-key rows read "new key" instead of "old key".
 * Shares that refresh (in-flight dedup), so it adds no @keys traffic.
 */
export async function refreshConversationKey(
  address: string,
  isGroup: boolean,
  selfUsername: string,
  userId: number
): Promise<void> {
  let keyOwner: string | undefined;
  if (isGroup) {
    const canonical = normalizeGroupAddress(address);
    keyOwner = (await getSymmetricKey(canonical))?.ownerUsername ?? (await findGroupByAddress(address))?.ownerName;
  } else if (!isExternalDmAddress(address)) {
    keyOwner = address;
  }
  if (keyOwner && keyOwner !== selfUsername && !wasRefreshedThisSession(keyOwner)) {
    await refreshPublicKey(keyOwner, userId);
  }
}

/**
 * True when we hold a group key safe to send with. With `refreshOwner` (group open), re-fetches
 * the owner's @keys first unless already done this session: an owner rotation marks the key
 * stale — the only signal a member who mostly sends gets. The owner's own key is never stale.
 */
export async function hasCurrentGroupKey(
  groupAddress: string,
  userId: number,
  selfUsername: string,
  refreshOwner = false
): Promise<boolean> {
  const canonical = normalizeGroupAddress(groupAddress);
  let entry = await getSymmetricKey(canonical);
  if (!entry || !entry.ownerUsername || entry.ownerUsername === selfUsername) return entry !== null;
  if (refreshOwner && !wasRefreshedThisSession(entry.ownerUsername)) {
    await refreshPublicKey(entry.ownerUsername, userId);
    entry = await getSymmetricKey(canonical);
  }
  return entry !== null && !entry.stale;
}

// Member side: group → owner key timestamp of this session's request still waiting for an
// answer. In memory on purpose: Android drops a request whose sender's key it doesn't hold yet
// (fetching it for next time), so each page session may ask once more while still stuck.
const pendingRequests = new Map<string, number>();

function clearPendingGroupKeyRequest(canonicalAddress: string): void {
  pendingRequests.delete(canonicalAddress);
}

/**
 * Asks the owner for the group key — the one entry point for every member-side trigger. At most
 * one request per group per owner key pair per page session: cleared when any distribution
 * arrives, moot once the owner rotates. `force` (the manual sync) re-fetches the owner's key and
 * always sends. Never re-sent on a timer.
 */
export async function requestGroupKey(
  groupAddress: string,
  selfUsername: string,
  userId: number,
  groupName?: string,
  force = false
): Promise<void> {
  const canonical = normalizeGroupAddress(groupAddress);
  try {
    const group = (await findGroupByAddress(groupAddress))
      ?? (groupName ? (await getGroups()).find((g) => g.name === groupName) : undefined);
    const owner = group?.ownerName;
    if (!group || !owner || owner === selfUsername) return;

    // Needed to unwrap the answer; its timestamp scopes the request to this owner key pair.
    if (force || !(await getPublicKeySetByUsername(owner))) await refreshPublicKey(owner, userId);
    const ownerTimestamp = Number((await getPublicKeySetByUsername(owner))?.timestampGenerated ?? 0);

    if (!force && pendingRequests.get(canonical) === ownerTimestamp) return; // already waiting
    pendingRequests.set(canonical, ownerTimestamp); // set before sending so a burst sends once
    await sendGroupKeyRequest(owner, group.id, userId).catch((err) => {
      pendingRequests.delete(canonical);
      throw err;
    });
  } catch (err) {
    console.warn("[GroupKey] Key request failed:", err);
  }
}

// Member side: the r_msgid the login catch-up resumes from.
const catchUpWatermarkKey = (userId: number) => `sl4w:gk:catchUp:${userId}`;
const CATCH_UP_LIMIT = 200;

/**
 * Applies group-key distributions that arrived while web was closed — web otherwise only sees
 * them live. Scans received messages since the last run and applies each group's latest
 * distribution — even over a key that looks current, since an owner rotation isn't detected
 * until later. Returns the canonical addresses whose stored key actually changed.
 */
export async function catchUpGroupKeys(userId: number, selfUsername: string): Promise<string[]> {
  const since = readRecord<number>(catchUpWatermarkKey(userId)).rmsgId ?? 0;
  const rows = await fetchReceivedSince(since, CATCH_UP_LIMIT);

  let next = since;
  const latestByGroup = new Map<string, { msgId: number; parsed: GroupKeyDistributionMessage }>();
  for (const row of rows) {
    next = Math.max(next, Number(row.msg_id) + 1);
    if (row.message.length > 4096) continue; // skip attachments cheaply
    const parsed = parseGroupKeyDistributionMessage(row.message);
    const prev = parsed && latestByGroup.get(parsed.group_name);
    if (parsed && (!prev || row.msg_id > prev.msgId)) latestByGroup.set(parsed.group_name, { msgId: row.msg_id, parsed });
  }

  const stored: string[] = [];
  for (const { parsed } of latestByGroup.values()) {
    try {
      const group = await resolveGroupByName(parsed.group_name);
      if (!group?.ownerName || group.ownerName === selfUsername) continue;
      const before = (await getSymmetricKey(normalizeGroupAddress(group.address)))?.symmetricKeyB64;
      const storedAt = await receiveGroupKeyDistributionMessage(parsed, userId, group.ownerName);
      if (storedAt && (await getSymmetricKey(storedAt))?.symmetricKeyB64 !== before) stored.push(storedAt);
    } catch (err) {
      console.warn(`[GroupKey] Catch-up failed for group "${parsed.group_name}":`, err);
    }
  }

  writeRecord(catchUpWatermarkKey(userId), { rmsgId: next });
  return stored;
}

/**
 * Owner-side manual sync: re-sends the group key to every member, each wrapped for their freshly
 * fetched key. Answers requests that arrived while we were offline — their DM threads are hidden
 * and a catch-up row names the requester only by address id, so we can't target just them.
 */
export async function redistributeGroupKey(groupAddress: string, selfUsername: string, userId: number): Promise<void> {
  const group = await findGroupByAddress(groupAddress);
  if (!group || group.ownerName !== selfUsername) return;
  await getOrGenerateGroupSymmetricKey(group.address, group.name, selfUsername);
  const members = group.members.filter((m) => !m.isOwner && m.username !== selfUsername);
  await Promise.all(members.map((m) => refreshPublicKey(m.username, userId)));
  await distributeGroupKeyToMembers(group.address, group.name, selfUsername, group.members, userId);
}

/**
 * Sends a group_key_request to the group owner (callers go through requestGroupKey).
 * Keyed by numeric group id to match Android's GroupKeyRequestRecordDetails (development).
 */
async function sendGroupKeyRequest(
  ownerUsername: string,
  groupId: number,
  userId: number
): Promise<void> {
  const msg = JSON.stringify({ cls: GROUP_KEY_REQUEST_CLS, group_id: groupId });
  await sendUserMessage({
    cmd: "SendUserMessage",
    message: msg,
    address: ownerUsername,
    addressId: 0,
    address_type: USER_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
  });
  console.log(`[GroupKey] Sent group_key_request for group ${groupId} to owner "${ownerUsername}"`);
}

/**
 * When the group owner receives a group_key_request, sends the symmetric key
 * to the requester encrypted for them individually.
 * Matches Android's DefaultGroupKeyExchangeManager.generateRequestedSymmetricKeyMessages().
 *
 * @param requesterUsername  Username of the member requesting the key
 * @param groupAddress       Routing address of the group
 * @param groupName          Human-readable group name
 * @param ownerUsername      Current user's username (must be the group owner)
 * @param userId             Current user's numeric ID
 */
export async function respondToGroupKeyRequest(
  requesterUsername: string,
  groupAddress: string,
  groupName: string,
  ownerUsername: string,
  userId: number,
  forceRequesterKeyRefresh = false
): Promise<boolean> {
  const ownKeySet = await getActiveKeySet(userId);
  if (!ownKeySet) {
    console.warn("[GroupKey] Cannot respond to key request — no own key set");
    return false;
  }

  // Check whether the group key already exists before generating. If it doesn't
  // exist, getOrGenerateGroupSymmetricKey creates a NEW key — which means all
  // other web members still have the old key and can't decrypt anything Android
  // sends. In that case, redistribute to every member so everyone stays in sync.
  const canonical = normalizeGroupAddress(groupAddress);
  const existingKey = await getSymmetricKey(canonical);

  const symmetricKeyB64 = await getOrGenerateGroupSymmetricKey(
    groupAddress,
    groupName,
    ownerUsername
  );

  if (!existingKey) {
    // Key was just generated — distribute to all members so web users don't end
    // up with a stale key while Android gets the new one.
    console.log(`[GroupKey] New key generated responding to request from "${requesterUsername}" — redistributing to all members`);
    try {
      const group = await findGroupByAddress(groupAddress);
      if (group) {
        await distributeGroupKeyToMembers(groupAddress, groupName, ownerUsername, group.members, userId);
        return true;
      }
    } catch (err) {
      console.error("[GroupKey] Full redistribution failed, falling through to single-recipient send:", err);
    }
  }

  // Key was pre-existing — send only to the requester (all other members already have it).
  // A repeat request means our last answer didn't land (likely wrapped for a key they no
  // longer hold), so re-fetch; a first answer uses the cached key if we have one.
  if (forceRequesterKeyRefresh || !(await getPublicKeySetByUsername(requesterUsername))) {
    await refreshPublicKey(requesterUsername, userId);
  }
  const peerKeys = await getPublicKeySetByUsername(requesterUsername);
  if (!peerKeys) {
    console.warn(`[GroupKey] Cannot respond to key request — no public key for "${requesterUsername}" after fetch attempt`);
    return false;
  }

  const msgContent = await createGroupKeyDistributionContent(
    groupName,
    symmetricKeyB64,
    ownerUsername,
    ownKeySet.encryptPrivateKeyJwk,
    ownKeySet.signPrivateKeyJwk,
    peerKeys.encryptPublicKeyJwk
  );

  await sendUserMessage({
    cmd: "SendUserMessage",
    message: msgContent,
    address: requesterUsername,
    addressId: 0,
    address_type: USER_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
    // Same r_encrypted=1 requirement as distributeGroupKeyToMembers — see that
    // function for the Android-side rationale.
    encrypted: 1,
  });
  console.log(`[GroupKey] Responded to key request for "${groupName}" from "${requesterUsername}"`);
  return true;
}

/**
 * A group_key_request the current user received, reduced to what the owner needs to answer it.
 * `answeredAt` (history only): group name → `created` of our latest distribution in the thread.
 */
export interface PendingKeyRequest {
  requesterUsername: string;
  groupId: number;
  msgId: number;
  created?: string;
  answeredAt?: Record<string, string>;
}

// Owner side: "requester|groupId" → msgId of the latest request answered.
const answeredRequestsKey = (ownerId: number) => `sl4w:gk:answeredRequests:${ownerId}`;

/**
 * Owner-side decision + response for a single group_key_request: verifies the current user
 * owns the referenced group and the requester is an actual member, then sends the key unless
 * this or a later request was already answered (one answer satisfies all earlier ones).
 * Shared by the live WebSocket path and the history sweep so the ownership/membership gate
 * lives in exactly one place. No-ops (does not throw) when we don't own the group.
 */
export async function answerGroupKeyRequest(params: PendingKeyRequest & {
  ownerUsername: string;
  ownerId: number;
}): Promise<void> {
  const { requesterUsername, groupId, msgId, created, answeredAt, ownerUsername, ownerId } = params;
  const groups = await getGroups();
  const ownedGroup = groups.find((g) => g.ownerName === ownerUsername && g.id === groupId);
  if (!ownedGroup) {
    console.debug(`[GroupKey] Ignoring group_key_request for group ${groupId} — current user is not the owner`);
    return;
  }
  // Only hand the key to actual members — otherwise any authenticated user could obtain any
  // group's key with a forged request. members includes the isOwner-flagged owner too.
  if (!ownedGroup.members.some((m) => m.username === requesterUsername)) {
    console.warn(`[GroupKey] Refusing group_key_request for group ${groupId} — requester "${requesterUsername}" is not a member`);
    return;
  }

  const recordKey = `${requesterUsername}|${groupId}`;
  await withLock(`sl4w-gk-answer:${recordKey}`, async () => {
    const answered = readRecord<number>(answeredRequestsKey(ownerId));
    if ((answered[recordKey] ?? -1) >= msgId) return;
    // `created` is a fixed-width router timestamp, so strings compare chronologically.
    const lastAnswer = answeredAt?.[ownedGroup.name];
    const answeredInThread = lastAnswer !== undefined && created !== undefined && lastAnswer > created;
    const handled = answeredInThread || await respondToGroupKeyRequest(
      requesterUsername, ownedGroup.address, ownedGroup.name, ownerUsername, ownerId, answered[recordKey] !== undefined
    );
    // Record only once it's actually answered, so a failed send is retried on the next load.
    if (handled && Number.isFinite(msgId)) {
      answered[recordKey] = msgId;
      writeRecord(answeredRequestsKey(ownerId), answered);
    }
  });
}

/**
 * Answers group_key_requests found in a freshly-loaded conversation history. This closes the
 * gap where a member requested the key while the owner was offline: the request sits in the
 * owner's DM and is answered the next time the owner loads that conversation (the live path
 * only fires when the owner is online at request time). Only each group's latest unanswered
 * request is answered; a non-owner load is a cheap no-op (answerGroupKeyRequest bails on
 * non-owned groups). Mirrors Android's owner-side handling of requests that arrive in a sync.
 */
export async function answerPendingGroupKeyRequests(
  requests: PendingKeyRequest[],
  ownerUsername: string,
  ownerId: number
): Promise<void> {
  await Promise.all(
    requests.map((req) =>
      answerGroupKeyRequest({ ...req, ownerUsername, ownerId }).catch((err) =>
        console.error("[GroupKey] Failed to answer pending group_key_request:", err)
      )
    )
  );
}
